/**
 * Config for the geo × program SEO landing pages (/programs/*).
 *
 * PROGRAMS keys are the URL slug for the program hub/state pages (short,
 * keyword-clean). `dbSlug` is the program's slug in the API (used to deep-link
 * a school card straight to its rebuilt program detail page). `id` is the
 * program_id the public-university-list / program-geo-counts endpoints take.
 */
export const PROGRAMS = [
  {
    slug: 'practical-nursing', icon: 'nursing', id: 17, dbSlug: 'licensed-practical-nursing',
    title: 'Licensed Practical Nurse', abbr: 'LPN', short: 'LPN',
    career: 'licensed practical nurse', accent: '#3b86f5', accentRgb: '59, 134, 245',
    intro: 'A Practical Nursing (LPN) program is the quickest licensed nursing credential, often about a year, and a common first rung toward RN.',
    salary: 60790
  },
  {
    slug: 'nursing-adn', icon: 'nursing', id: 21, dbSlug: 'associate-degree-nursing',
    title: 'Associate Degree in Nursing', abbr: 'ADN', short: 'ADN',
    career: 'registered nurse', accent: '#3b86f5', accentRgb: '59, 134, 245',
    intro: 'An Associate Degree in Nursing (ADN) is the fastest route to becoming a registered nurse. Two years, and you qualify to sit for the NCLEX-RN.',
    salary: 79000
  },
  {
    slug: 'nursing-bsn', icon: 'nursing', id: 35, dbSlug: 'bachelor-of-science-nursing',
    title: 'Bachelor of Science in Nursing', abbr: 'BSN', short: 'BSN',
    career: 'registered nurse', accent: '#3b86f5', accentRgb: '59, 134, 245',
    intro: 'A Bachelor of Science in Nursing (BSN) is the four-year RN degree most hospitals prefer, and the standard step toward advanced practice.',
    salary: 94000
  },
  {
    slug: 'dental-hygiene', icon: 'dental-hygiene', id: 99, dbSlug: 'dental-hygiene',
    title: 'Dental Hygiene', abbr: 'RDH', short: 'Dental Hygiene',
    career: 'dental hygienist', accent: '#3b86f5', accentRgb: '59, 134, 245',
    intro: 'Dental Hygiene programs lead to licensure (RDH) and one of the best-paid associate-level careers in healthcare.',
    salary: 87000
  },
  {
    slug: 'radiologic-technology', icon: 'radiologic-technology', id: 79, dbSlug: 'radiologic-technology',
    title: 'Radiologic Technology', abbr: 'RT(R)', short: 'Rad Tech',
    career: 'radiologic technologist', accent: '#3b86f5', accentRgb: '59, 134, 245',
    intro: 'Radiologic Technology programs train you to perform medical imaging (X-ray and beyond) and sit for the ARRT credentialing exam.',
    salary: 73000
  },
  {
    slug: 'respiratory-therapy', icon: 'respiratory-therapy', id: 11, dbSlug: 'respiratory-therapist',
    title: 'Respiratory Therapist', abbr: 'RRT', short: 'Respiratory',
    career: 'respiratory therapist', accent: '#3b86f5', accentRgb: '59, 134, 245',
    intro: 'Respiratory Therapy programs prepare you to treat patients with breathing and cardiopulmonary disorders and to earn the RRT credential.',
    salary: 63000
  },
  {
    slug: 'sonography', icon: 'sonography', id: 100, dbSlug: 'diagnostic-medical-sonography',
    title: 'Diagnostic Medical Sonography', abbr: 'DMS', short: 'Sonography',
    career: 'diagnostic medical sonographer', accent: '#3b86f5', accentRgb: '59, 134, 245',
    intro: 'Diagnostic Medical Sonography (ultrasound) programs train you to produce diagnostic images and pursue ARDMS certification.',
    salary: 84000
  },
  {
    slug: 'physical-therapy-assistant', icon: 'physical-therapy', id: 20, dbSlug: 'physical-therapy-assistant',
    title: 'Physical Therapist Assistant', abbr: 'PTA', short: 'Physical Therapy',
    career: 'physical therapist assistant', accent: '#3b86f5', accentRgb: '59, 134, 245',
    intro: 'Physical Therapist Assistant programs offer strong clinical pay with no overnight shifts, and require observation hours and prereqs up front.',
    salary: 65000
  },
  {
    slug: 'paramedic', icon: 'paramedic', id: 19, dbSlug: 'paramedic',
    title: 'Paramedic', abbr: 'EMT-P', short: 'Paramedic',
    career: 'paramedic', accent: '#3b86f5', accentRgb: '59, 134, 245',
    intro: 'Paramedic programs build on an EMT card and field experience to train you in advanced life support and prepare you for the NREMT Paramedic exam.',
    salary: 58410
  }
]

// Picker sections: the RN/LPN credentials, then everything else.
export const NURSING_ABBRS = ['LPN', 'ADN', 'BSN']
export function programGroup(abbr) { return NURSING_ABBRS.includes(abbr) ? 'Nursing' : 'Allied health' }
export const byGroupThenCount = (a, b) => (a.group === b.group ? 0 : a.group === 'Nursing' ? -1 : 1) || (b.count - a.count) || a.label.localeCompare(b.label)

export const PROGRAM_BY_SLUG = PROGRAMS.reduce((m, p) => { m[p.slug] = p; return m }, {})

// Keep API abbreviations aligned with the launch program categories.
export function launchProgramFor(program = {}) {
  return (program.id != null && PROGRAMS.find(item => String(item.id) === String(program.id))) ||
    (program.slug && PROGRAMS.find(item => [item.slug, item.dbSlug].includes(program.slug))) ||
    (program.title && PROGRAMS.find(item => item.title.toLowerCase() === program.title.trim().toLowerCase())) ||
    (program.abbr && PROGRAMS.find(item => item.abbr.toLowerCase() === program.abbr.trim().toLowerCase())) || null
}

// US states + DC. `name` matches the University.state values in the DB.
export const STATES = [
  ['alabama', 'Alabama'], ['alaska', 'Alaska'], ['arizona', 'Arizona'], ['arkansas', 'Arkansas'],
  ['california', 'California'], ['colorado', 'Colorado'], ['connecticut', 'Connecticut'],
  ['delaware', 'Delaware'], ['district-of-columbia', 'District Of Columbia'], ['florida', 'Florida'],
  ['georgia', 'Georgia'], ['hawaii', 'Hawaii'], ['idaho', 'Idaho'], ['illinois', 'Illinois'],
  ['indiana', 'Indiana'], ['iowa', 'Iowa'], ['kansas', 'Kansas'], ['kentucky', 'Kentucky'],
  ['louisiana', 'Louisiana'], ['maine', 'Maine'], ['maryland', 'Maryland'], ['massachusetts', 'Massachusetts'],
  ['michigan', 'Michigan'], ['minnesota', 'Minnesota'], ['mississippi', 'Mississippi'], ['missouri', 'Missouri'],
  ['montana', 'Montana'], ['nebraska', 'Nebraska'], ['nevada', 'Nevada'], ['new-hampshire', 'New Hampshire'],
  ['new-jersey', 'New Jersey'], ['new-mexico', 'New Mexico'], ['new-york', 'New York'],
  ['north-carolina', 'North Carolina'], ['north-dakota', 'North Dakota'], ['ohio', 'Ohio'],
  ['oklahoma', 'Oklahoma'], ['oregon', 'Oregon'], ['pennsylvania', 'Pennsylvania'], ['rhode-island', 'Rhode Island'],
  ['south-carolina', 'South Carolina'], ['south-dakota', 'South Dakota'], ['tennessee', 'Tennessee'],
  ['texas', 'Texas'], ['utah', 'Utah'], ['vermont', 'Vermont'], ['virginia', 'Virginia'],
  ['washington', 'Washington'], ['west-virginia', 'West Virginia'], ['wisconsin', 'Wisconsin'],
  ['wyoming', 'Wyoming']
]
export const STATE_NAME_BY_SLUG = STATES.reduce((m, [s, n]) => { m[s] = n; return m }, {})
export const STATE_SLUG_BY_NAME = STATES.reduce((m, [s, n]) => { m[n.toLowerCase()] = s; return m }, {})

export function stateSlugify(name) {
  return STATE_SLUG_BY_NAME[(name || '').toLowerCase()] || null
}

export const SITE_URL = "https://transitionu.health"
